import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../shared/Modal';
import { submissionService } from '../../services/submissionService';
import { reportService } from '../../services/reportService';
import { formatPeriodLabel, SUBMISSION_PERIODS } from './periodFormat';
import { mapSupabaseError } from '../../utils/supabaseErrors';

export function NewSubmissionChooser({ assignedSystems, piSystems = [], reportSystems = [], onContinue, onClose }) {
  const [type, setType] = useState('kpi');
  const [systemId, setSystemId] = useState(assignedSystems[0]?.id || '');
  const [piSystemId, setPiSystemId] = useState(piSystems[0]?.id || '');
  const [reportSystemId, setReportSystemId] = useState(reportSystems[0]?.id || '');
  const [period, setPeriod] = useState('');
  const [periods, setPeriods] = useState([]);
  const [instanceIdx, setInstanceIdx] = useState(0);
  const [error, setError] = useState('');

  const isPerf = type === 'performance';
  const isReport = type === 'report';

  const systemsForType = isReport ? reportSystems : isPerf ? piSystems : assignedSystems;
  const selectedSystemId = isReport ? reportSystemId : isPerf ? piSystemId : systemId;

  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState('');

  const selectedSystem = useMemo(
    () => systemsForType.find((s) => s.id === selectedSystemId),
    [systemsForType, selectedSystemId]
  );
  const needsCountry = isReport && !!selectedSystem?.per_country;

  // Load the central country list the first time a per-country system is chosen.
  useEffect(() => {
    if (!needsCountry || countries.length > 0) return;
    let active = true;
    reportService.getCountries().then((rows) => { if (active) setCountries(rows || []); });
    return () => { active = false; };
  }, [needsCountry, countries.length]);

  // Period options come from the selected system's enabled_periods (in the
  // canonical order), intersected with the known set for safety. Memoize on the
  // stable values (the systems array + selected id), not the find() result —
  // a fresh object reference each render would defeat the memo and re-fire the
  // dependent effect on every render.
  const availablePeriods = useMemo(() => {
    const sys = systemsForType.find((s) => s.id === selectedSystemId);
    const enabled = sys?.enabled_periods || [];
    return SUBMISSION_PERIODS.filter((p) => enabled.includes(p));
  }, [systemsForType, selectedSystemId]);

  // Keep `period` valid as the selected system (and thus its periods) changes.
  useEffect(() => {
    if (availablePeriods.length === 0) { setPeriod(''); return; }
    setPeriod((prev) => (availablePeriods.includes(prev) ? prev : availablePeriods[0]));
  }, [availablePeriods]);

  // Load the date windows for the chosen period.
  useEffect(() => {
    if (!period) { setPeriods([]); setInstanceIdx(0); return; }
    let active = true;
    setError('');
    setPeriods([]);
    setInstanceIdx(0);
    submissionService.recentPeriods(period, 4)
      .then((rows) => { if (active) { setPeriods(rows); setInstanceIdx(0); } })
      .catch((e) => { if (active) setError(mapSupabaseError(e)); });
    return () => { active = false; };
  }, [period, type]);

  const chosen = periods[instanceIdx];
  const canContinue = !!selectedSystemId && !!period && !!chosen && (!needsCountry || !!countryId);

  const handleContinue = () => {
    if (!canContinue) return;
    if (isReport) {
      onContinue({ type: 'report', period, reportSystemId: selectedSystemId, periodStart: chosen.period_start, periodEnd: chosen.period_end, countryId: needsCountry ? countryId : null });
    } else if (isPerf) {
      onContinue({ type: 'performance', period, piSystemId: selectedSystemId, periodStart: chosen.period_start, periodEnd: chosen.period_end });
    } else {
      onContinue({ type: 'kpi', period, kpiSystemId: selectedSystemId, periodStart: chosen.period_start, periodEnd: chosen.period_end });
    }
  };

  const setSelectedSystem = (id) => {
    if (isReport) setReportSystemId(id);
    else if (isPerf) setPiSystemId(id);
    else setSystemId(id);
  };

  return (
    <Modal onClose={onClose} role="dialog" aria-modal="true" aria-label="New submission">
      <h3>New submission</h3>
      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="form-group">
        <label htmlFor="ns-type">Type</label>
        <select id="ns-type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="kpi">KPI</option>
          <option value="performance" disabled={piSystems.length === 0}>Performance Data</option>
          <option value="report">Report</option>
        </select>
      </div>

      {systemsForType.length > 1 && (
        <div className="form-group">
          <label htmlFor="ns-system">{isReport ? 'Report system' : isPerf ? 'PI system' : 'KPI system'}</label>
          <select id="ns-system" value={selectedSystemId} onChange={(e) => setSelectedSystem(e.target.value)}>
            {systemsForType.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}

      {needsCountry && (
        <div className="form-group">
          <label htmlFor="ns-country">Country</label>
          <select id="ns-country" value={countryId} onChange={(e) => setCountryId(e.target.value)}>
            <option value="">— Select —</option>
            {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="ns-period">Period</label>
        <select id="ns-period" value={period} onChange={(e) => setPeriod(e.target.value)} disabled={availablePeriods.length <= 1}>
          {availablePeriods.length === 0 && <option value="">No periods enabled</option>}
          {availablePeriods.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {availablePeriods.length === 0 && (
          <small role="alert">This system has no enabled periods.</small>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="ns-instance">Which {period ? period.toLowerCase().replace('ly', '') : 'period'}</label>
        <select id="ns-instance" value={instanceIdx} onChange={(e) => setInstanceIdx(Number(e.target.value))}>
          {periods.map((p, i) => (
            <option key={p.period_start} value={i}>{formatPeriodLabel(period, p.period_start, p.period_end)}</option>
          ))}
        </select>
      </div>

      <div className="modal-buttons">
        <button type="button" className="btn-edit" onClick={handleContinue} disabled={!canContinue}>Continue →</button>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}
